import React, { useState, useRef, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { ScrollArea } from './ui/scroll-area'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { Badge } from './ui/badge'
import CandidateTiles from './CandidateTiles'
import { Send, Bot, User, Sparkles, FileText, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import {
  detectJobDescription,
  processJobDescription,
  processConversationalRequirements,
  formatRequirementsSummary,
  fetchCandidateMatches,
  generateMockCandidates
} from '../utils/hiringAgentApi'

const AIHiringAgent = ({
  userInfo,
  macrosByCategories
}) => {
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m your AI hiring assistant. I can help you source candidates based on job titles or job descriptions. What position are you looking to fill?',
      timestamp: new Date().toISOString()
    }
  ])
  const [inputValue, setInputValue] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [icpRequirements, setIcpRequirements] = useState(null);

  // New state for API workflow
  const [inputMode, setInputMode] = useState(null) // 'jd' | 'requirements' | null
  const [userMessages, setUserMessages] = useState([]) // Store all user messages for API calls
  const [currentRequirements, setCurrentRequirements] = useState(null)
  const [joid] = useState('hiring-agent-job-' + Date.now()) // Job ID for API calls
  const [expandedMessages, setExpandedMessages] = useState(new Set()) // Track which messages are expanded
  
  const messagesEndRef = useRef(null)

  const userId = userInfo?.uid ?? '';
  console.log('User ID for hiring agent:', userId);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Constants for text truncation
  const CHARACTER_LIMIT = 300

  // Helper functions
  const toggleMessageExpansion = (messageId) => {
    const newExpandedMessages = new Set(expandedMessages)
    if (newExpandedMessages.has(messageId)) {
      newExpandedMessages.delete(messageId)
    } else {
      newExpandedMessages.add(messageId)
    }
    setExpandedMessages(newExpandedMessages)
  }

  const shouldTruncateMessage = (content) => {
    return content.length > CHARACTER_LIMIT
  }

  const getTruncatedContent = (content) => {
    return content.substring(0, CHARACTER_LIMIT) + '...'
  }

  const MessageContent = ({ message }) => {
    const isExpanded = expandedMessages.has(message.id)
    const shouldTruncate = shouldTruncateMessage(message.content)
    const isUserMessage = message.role === 'user'
    
    // Only apply truncation to user messages
    if (!isUserMessage || !shouldTruncate) {
      return (
        <div className="whitespace-pre-wrap text-gray-800">
          {message.content}
        </div>
      )
    }

    return (
      <div className="space-y-2">
        <div className="whitespace-pre-wrap text-gray-800">
          {isExpanded ? message.content : getTruncatedContent(message.content)}
        </div>
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleMessageExpansion(message.id)}
            className="text-blue-600 hover:text-blue-800 p-0 h-auto font-medium"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show more
              </>
            )}
          </Button>
          {!isExpanded && (
            <span className="text-xs text-gray-500">
              {message.content.length} characters
            </span>
          )}
        </div>
      </div>
    )
  }


  const handleSendMessage = async () => {
    if (!inputValue.trim()) return
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    }

    setMessages(prev => [...prev, userMessage])
    const currentInput = inputValue
    setInputValue('')
    setIsLoading(true)

    try {
      // Add to user messages for API calls
      const newUserMessages = [...userMessages, currentInput]
      setUserMessages(newUserMessages)

      // If this is the first message, detect if it's a JD or requirements
      if (inputMode === null) {
        console.log('🔍 Detecting input type for:', currentInput.substring(0, 50))
        const isJD = await detectJobDescription(currentInput, userId)
        const detectedMode = isJD ? 'jd' : 'requirements'
        setInputMode(detectedMode)
        
        console.log('🎯 Input mode detected:', detectedMode)
        
        // Process based on detected mode
        let apiResult
        if (isJD) {
          apiResult = await processJobDescription(newUserMessages.join('\n'), userId)
        } else {
          apiResult = await processConversationalRequirements(newUserMessages, userId)
        }
        
        // Generate candidates based on API response
        if (apiResult && apiResult.search_gen_response) {
          setCandidatesLoading(true)
          try {
            const candidateData = await fetchCandidateMatches(
              apiResult.search_gen_response.icp_requirements,
              userId,
              10,
              macrosByCategories
            )
            setIcpRequirements(apiResult.search_gen_response.icp_requirements);
            setCandidates(candidateData.candidates)
            // Store total matches count for display
            setCurrentRequirements({
              ...apiResult.search_gen_response,
              total_matches: candidateData.total_matches,
              processed_count: candidateData.processed_count
            })
          } catch (error) {
            console.error('Error fetching candidates:', error)
            // Fallback to mock candidates
            const fallbackCandidates = generateMockCandidates(apiResult.search_gen_response)
            setCandidates(fallbackCandidates)
          } finally {
            setCandidatesLoading(false)
          }
        }
        
        // Generate AI response
        const aiResponse = generateAIResponseFromAPI(apiResult, isJD, newUserMessages.length)
        setMessages(prev => [...prev, aiResponse])
      } else {
        // Subsequent messages - use established input mode
        console.log('🔄 Processing follow-up message in mode:', inputMode)
        
        let apiResult
        if (inputMode === 'jd') {
          apiResult = await processJobDescription(newUserMessages.join('\n'), userId)
        } else {
          apiResult = await processConversationalRequirements(newUserMessages, userId)
        }
        
        // Update candidates if we got new results
        if (apiResult && apiResult.search_gen_response) {
          setCandidatesLoading(true)
          try {
            const candidateData = await fetchCandidateMatches(
              apiResult.search_gen_response.icp_requirements,
              userId,
              10,
              macrosByCategories
            )
            setCandidates(candidateData.candidates)
            // Store total matches count for display
            setCurrentRequirements({
              ...apiResult.search_gen_response,
              total_matches: candidateData.total_matches,
              processed_count: candidateData.processed_count
            })
          } catch (error) {
            console.error('Error fetching candidates:', error)
            // Fallback to mock candidates
            const fallbackCandidates = generateMockCandidates(apiResult.search_gen_response)
            setCandidates(fallbackCandidates)
          } finally {
            setCandidatesLoading(false)
          }
        }
        
        const aiResponse = generateAIResponseFromAPI(apiResult, inputMode === 'jd', newUserMessages.length)
        setMessages(prev => [...prev, aiResponse])
      }
    } catch (error) {
      console.error('Error processing message:', error)
      
      // Fallback to mock response
      const fallbackResponse = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an issue processing your request. Let me help you with a basic search instead. Could you provide more details about the role you\'re looking to fill?',
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, fallbackResponse])
    } finally {
      setIsLoading(false)
    }
  }

  const generateAIResponseFromAPI = (apiResult, isJD, messageCount) => {
    let content = ''
    
    if (messageCount === 1) {
      // First interaction - show what was detected and extracted
      if (isJD) {
        content = `Great! I've analyzed your job description and extracted the key requirements:\n\n${formatRequirementsSummary(apiResult?.search_gen_response)}\n\nI'm now searching our candidate database for profiles that match these criteria. You'll see the best matches in the candidate tiles above, ranked by relevance and compatibility.`
      } else {
        content = `Thanks for that information! I'm processing your requirements and searching for relevant candidates. To provide you with the most accurate matches, I may ask for a few more details as we go.\n\nBased on what you've shared so far, I'm finding candidates that align with your needs. You can see the initial matches in the tiles above.`
      }
    } else {
      // Follow-up interactions
      const summary = formatRequirementsSummary(apiResult?.search_gen_response)
      content = `Perfect! I've updated the search based on your additional requirements:\n\n${summary}\n\nThe candidate tiles above show the refreshed results with updated match scores. Each candidate has been re-evaluated against your complete criteria.`
    }

    return {
      id: Date.now().toString(),
      role: 'assistant',
      content,
      timestamp: new Date().toISOString()
    }
  }


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Sparkles className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Hiring Agent</h1>
            <p className="text-gray-600">Conversational candidate sourcing powered by AI</p>
          </div>
        </div>
      </div>

      {/* Candidate Tiles - Always visible at top when available */}
      {(candidates.length > 0 || candidatesLoading) && (
        <div className="bg-white border-b border-gray-200">
          <CandidateTiles 
            candidates={candidates} 
            isLoading={candidatesLoading} 
            totalMatches={currentRequirements?.total_matches}
            macrosByCategories={macrosByCategories}
            icpRequirements={icpRequirements}
            userId={userId}
          />
        </div>
      )}

      {/* Chat Interface */}
      <div className="flex-1 flex flex-col">
        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex space-x-3 max-w-3xl ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Avatar className="h-8 w-8 mt-1">
                    {message.role === 'assistant' ? (
                      <>
                        <AvatarFallback className="bg-blue-100 text-blue-600">
                          <Bot className="h-4 w-4" />
                        </AvatarFallback>
                      </>
                    ) : (
                      <>
                        <AvatarFallback className="bg-gray-100 text-gray-600">
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </>
                    )}
                  </Avatar>
                  
                  <Card className={`${message.role === 'user' ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                    <CardContent className="p-4">
                      {message.fileAttachment && (
                        <div className="flex items-center space-x-2 mb-2 text-sm text-gray-600">
                          <FileText className="h-4 w-4" />
                          <span>{message.fileAttachment}</span>
                        </div>
                      )}
                      <MessageContent message={message} />
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex space-x-3 max-w-3xl">
                  <Avatar className="h-8 w-8 mt-1">
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      <Bot className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                  <Card className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        <span className="text-gray-600">Processing your request...</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                {/* <div className="flex space-x-2 mb-2">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Upload JD
                  </Button>
                </div> */}
                
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe the position or paste job requirements..."
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                  rows={2}
                />
              </div>
              
              <Button 
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="h-12 px-6"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AIHiringAgent